import { Button, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteInvoiceArticle, getInvoiceArticleById, updateInvoiceArticle } from 'src/shared/services/invoiceArticleService';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { getAllInvoiceArticleCategory } from 'src/shared/services/invoiceArticleCategoryService';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { VatRateEnum } from 'src/shared/models/enums/vatRate.enum';
import { InvoiceArticleRequest } from '../models/invoiceArticleRequest';
import { InvoiceArticleCategory } from '../models/invoiceArticleCategory';
import { InvoiceArticleCategorySearchFilter } from '../models/invoiceArticleCategorySearchFilter';
import { VatRateSelector } from './vatRateSelector';

type EditInvoiceArticleProps = {
  articleId: number;
  setArticleId: (resetArticleId: undefined) => void;
  setOpenEditArticle: (openEditArticle: boolean) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
};

export const EditInvoiceArticle = (props: EditInvoiceArticleProps) => {
  const { articleId, setArticleId, setOpenEditArticle, setRefetchDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<InvoiceArticleCategory[]>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const categoryFilter = useMemo<InvoiceArticleCategorySearchFilter>(
    () => ({
      pageIndex: 0,
      pageSize: 1000,
      sortCol: 'articleCategory',
      sortDir: 'asc',
      filter: '',
    }),
    [],
  );

  const minimumCharRequired = 3;
  const maximumCharRequired = 20;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const schema = yup
    .object({
      article_id: yup.number().required(),
      name: yup.string().min(minimumCharRequired, minCharRequiredMessage).max(maximumCharRequired, maxCharRequiredMessage).required(),
      articleNr: yup.string().max(50, maxCharRequiredMessage).required(t('common:validationMessages.required')),
      price: yup
        .number()
        .typeError(t('common:validationMessages.amount'))
        .min(0, t('common:validationMessages.nonNegative'))
        .required(t('common:validationMessages.required')),
      articleCategory_id: yup.number().optional(),
      vatRate: yup.string().required(t('common:validationMessages.required')),
    })
    .required();

  const formMethods = useForm<InvoiceArticleRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      article_id: articleId,
      name: '',
      articleNr: '',
      price: 0,
      articleCategory_id: 0,
      vatRate: VatRateEnum.Zero,
    },
  });
  const { handleSubmit, setValue, watch } = formMethods;

  const watchedCategoryId = watch('articleCategory_id') ?? 0;
  const selectedArticleCategory = (categories ?? []).find((opt) => opt.articleCategory_id === watchedCategoryId) ?? null;

  const handleClose = () => {
    setArticleId(undefined);
    setOpenEditArticle(false);
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      await deleteInvoiceArticle(articleId);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleRegister.deleteSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('invoice:articleRegister.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoiceArticleCategory(categoryFilter);
      if (isMounted()) {
        const items = result.data.results;
        setCategories(items);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [categoryFilter, isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchCategories();
    }
  }, [fetchCategories, isMounted]);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInvoiceArticleById(articleId);

      if (isMounted()) {
        setValue('name', result.data.name);
        setValue('articleNr', result.data.articleNr);
        setValue('price', result.data.price);
        setValue('articleCategory_id', result.data.articleCategory_id);
        setValue('vatRate', result.data.vatRate ?? VatRateEnum.Zero);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [articleId, isMounted, setValue]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const onSubmit = async (data: InvoiceArticleRequest) => {
    setLoading(true);

    const dataToPost: InvoiceArticleRequest = {
      ...data,
      vatRate: data.price > 0 ? data.vatRate : VatRateEnum.Zero,
    };

    try {
      await updateInvoiceArticle(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleRegister.updateSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:articleRegister.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form id='update-article-form' onSubmit={handleSubmit(onSubmit)}>
          <GenericDialog
            maxWidth='sm'
            open
            onClose={handleClose}
            onSave={handleSubmit(onSubmit)}
            dialogTitle={t('invoice:articleRegister.editArticle')}
            dialogContent={
              <Grid container spacing={2}>
                <LoadingOverlay loading={loading} />
                <Grid item xs={12} sm={6}>
                  <ControlledTextField label={t('invoice:articleRegister.articleName')} name='name' />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ControlledTextField label={t('invoice:articleRegister.articleNumber')} name='articleNr' />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <ControlledTextField label={t('invoice:articleRegister.price')} type='number' name='price' />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <VatRateSelector name='vatRate' disable={watch('price') < 0} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AutocompleteDropdown<InvoiceArticleCategory>
                    keyProp='articleCategory_id'
                    disable={!categories || categories.length === 0}
                    value={selectedArticleCategory}
                    nameProp='articleCategory'
                    label={t('invoice:articleRegister.category')}
                    emptyLabel={categories?.length === 0 ? t('invoice:articleRegister.noCategories') : t('common:button.choose')}
                    placeholder={t('common:button.choose')}
                    name='articleCategory_id'
                    options={categories || []}
                    callback={(selectedOption) => {
                      if (selectedOption) {
                        setValue('articleCategory_id', selectedOption.articleCategory_id);
                      } else {
                        setValue('articleCategory_id', 0);
                      }
                    }}
                  />
                </Grid>
              </Grid>
            }
            dialogActions={
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Button size='large' variant='outlined' color='error' onClick={() => setOpenDeleteDialog(true)}>
                    {t('common:button.delete')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    form='update-article-form'
                    size='large'
                    sx={{ marginLeft: 2 }}
                    disabled={loading}
                    disableElevation
                    variant='contained'
                    color='primary'
                    type='submit'
                  >
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </Grid>
            }
          />
        </form>
      </FormProvider>

      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            handleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
